import { z } from "zod";

// Region labels double as Lua table keys and shared-dict key suffixes in the
// rendered nginx.conf, so they are restricted to a safe charset.
export const RegionRegex = /^[a-z0-9-]{2,32}$/;
export const CountryCodeRegex = /^[A-Z]{2}$/;

// Non-ISO country codes clients send that must share a pool with the ISO one.
export const COUNTRY_ALIASES: Record<string, string> = { UK: "GB" };

export function normalizeCountry(code: string): string {
	const c = code.trim().toUpperCase();
	return COUNTRY_ALIASES[c] ?? c;
}

export const RegionSchema = z
	.string()
	.trim()
	.toLowerCase()
	.regex(RegionRegex, "lowercase letters, digits and dashes (2-32 chars)");

/** Host region input: "" (from an empty form field) or null clears it. */
export const HostRegionSchema = z
	.union([z.literal(""), RegionSchema])
	.nullable()
	.optional();

export function normalizeHostRegion(
	v: string | null | undefined,
): string | null | undefined {
	if (v === undefined) return undefined;
	if (v === null) return null;
	const t = v.trim().toLowerCase();
	return t === "" ? null : t;
}

export const RegionFallbacksSchema = z
	.record(RegionSchema, z.array(RegionSchema).max(16))
	.refine((m) => Object.keys(m).length <= 64, "too many regions");

export const CountryRegionsSchema = z
	.record(z.string(), RegionSchema)
	.transform((m, ctx) => {
		const out: Record<string, string> = {};
		for (const [raw, region] of Object.entries(m)) {
			const country = normalizeCountry(raw);
			if (!CountryCodeRegex.test(country)) {
				ctx.addIssue({
					code: "custom",
					message: `invalid country code "${raw}" (expected 2 letters)`,
				});
				continue;
			}
			out[country] = region;
		}
		return out;
	})
	.refine((m) => Object.keys(m).length <= 300, "too many countries");

/**
 * Non-fatal config warnings: map entries that point at regions with no
 * enabled hosts. `hostRegions` is the region of every enabled host.
 */
export function geoRoutingWarnings(input: {
	hostRegions: Array<string | null>;
	countryRegions: Record<string, string>;
	regionFallbacks: Record<string, string[]>;
	/** Hostnames whose detected location maps to no pool. */
	unknownLocationHosts?: string[];
	/**
	 * Geo routing picks the backend for the cluster `/launch` entry, which is
	 * only rendered with cluster routing on. Omitted means on.
	 */
	clusterProxyEnabled?: boolean;
}): string[] {
	const out: string[] = [];
	for (const hostname of input.unknownLocationHosts ?? []) {
		out.push(
			`${hostname}: location unknown (no datacenter reported), set its region manually.`,
		);
	}

	const live = new Set(input.hostRegions.filter((r): r is string => !!r));
	if (live.size > 0 && input.clusterProxyEnabled === false) {
		out.push(
			"Geo routing is inactive: cluster routing is off, so there is no /launch entry to route. Turn on cluster routing to use regions.",
		);
	}
	const configured =
		Object.keys(input.countryRegions).length > 0 ||
		Object.keys(input.regionFallbacks).length > 0;
	if (live.size === 0) {
		if (configured) {
			out.push(
				"Geo routing is inactive: no enabled host has a region, so every request uses fleet-wide round-robin.",
			);
		}
		return out;
	}

	const deadCountries = new Map<string, string[]>();
	for (const [country, region] of Object.entries(input.countryRegions)) {
		if (live.has(region)) continue;
		const list = deadCountries.get(region) ?? [];
		list.push(country);
		deadCountries.set(region, list);
	}
	for (const [region, countries] of deadCountries) {
		const fb = (input.regionFallbacks[region] ?? []).find((f) => live.has(f));
		out.push(
			`${region} has no enabled hosts: ${countries.sort().join(", ")} ${
				fb ? `fall back to ${fb}` : "use fleet-wide round-robin"
			}.`,
		);
	}
	for (const [region, list] of Object.entries(input.regionFallbacks)) {
		const dead = list.filter((f) => !live.has(f));
		if (dead.length > 0) {
			out.push(
				`Fallbacks for ${region} with no enabled hosts (skipped): ${dead.join(", ")}.`,
			);
		}
	}
	return out;
}

// Pools are server locations, not Hetzner network zones: eu-central holds
// both Germany and Finland, which need separate pools for "DE first, then FI".
export const LOCATION_POOLS: Record<string, string> = {
	fsn1: "de",
	nbg1: "de",
	hel1: "fi",
	ash: "us-east",
	hil: "us-west",
	sin: "sg",
};

// Hetzner network zones that hold exactly one location, so the pool is known
// even when the metadata service has no availability-zone (older servers).
const SINGLE_LOCATION_ZONES: Record<string, string> = {
	"us-east": "us-east",
	"us-west": "us-west",
	"ap-southeast": "sg",
};

const HETZNER_NETWORK_ZONES = new Set([
	"eu-central",
	"us-east",
	"us-west",
	"ap-southeast",
]);

/**
 * Pool for a worker's reported location. `zone` is a Hetzner datacenter
 * ("fsn1-dc14"); `region` is either a Hetzner network zone or, for workers
 * configured with VEIL_REGION, already a pool name. Returns null when the
 * pool can't be told apart (eu-central without a zone).
 */
export function poolForLocation(
	region: string | null | undefined,
	zone: string | null | undefined,
): string | null {
	const location = zone?.trim().toLowerCase().split("-")[0];
	if (location && LOCATION_POOLS[location]) return LOCATION_POOLS[location];

	const r = region?.trim().toLowerCase();
	if (!r || !RegionRegex.test(r)) return null;
	if (SINGLE_LOCATION_ZONES[r]) return SINGLE_LOCATION_ZONES[r];
	if (HETZNER_NETWORK_ZONES.has(r)) return null;
	return r;
}

/** Manual region wins; otherwise the pool derived from the detected location. */
export function effectiveRegion(host: {
	region: string | null;
	detectedRegion: string | null;
	detectedZone: string | null;
}): string | null {
	return host.region || poolForLocation(host.detectedRegion, host.detectedZone);
}

/** Detected but unusable location: routed only by fleet-wide round-robin. */
export function isLocationUnknown(host: {
	region: string | null;
	detectedRegion: string | null;
	detectedZone: string | null;
}): boolean {
	return (
		!host.region && !!host.detectedRegion && effectiveRegion(host) === null
	);
}

export const DEFAULT_COUNTRY_REGIONS: Record<string, string> = {
	AT: "de",
	DE: "de",
	DK: "de",
	ES: "de",
	FR: "de",
	GB: "de",
	IE: "de",
	IT: "de",
	NL: "de",
	SE: "fi",
	US: "us-east",
	BR: "us-east",
	AU: "sg",
};

export const DEFAULT_REGION_FALLBACKS: Record<string, string[]> = {
	de: ["fi", "us-east"],
	fi: ["de", "us-east"],
	"us-east": ["us-west", "de"],
	"us-west": ["us-east"],
	sg: ["us-west", "us-east"],
};
